import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, X } from 'lucide-react';
import { Modal, ModalBody, ModalFooter, Button } from './ui';
import './ModelingStatusExitCommentModal.css';

const ModelingStatusExitCommentModal = ({
  isOpen,
  onClose,
  onSubmit,
  onSkip,
  fromStatus,
  toStatus,
  employees = [],
}) => {
  const [comment, setComment] = useState('');
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
  const [cursorPosition, setCursorPosition] = useState(0);
  const [mentionStartIndex, setMentionStartIndex] = useState(0);
  const textareaRef = useRef(null);
  const dropdownRef = useRef(null);

  // Filter employees based on @ mention search
  const filteredEmployees = employees.filter((emp) =>
    emp.name?.toLowerCase().includes(mentionSearch.toLowerCase())
  );

  // Handle textarea change
  const handleCommentChange = (e) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;
    setComment(value);
    setCursorPosition(cursorPos);

    // Check if user is typing @ mention
    const textBeforeCursor = value.substring(0, cursorPos);
    const atIndex = textBeforeCursor.lastIndexOf('@');

    if (atIndex !== -1 && atIndex === cursorPos - 1) {
      // User just typed @
      setMentionStartIndex(atIndex);
      setMentionSearch('');
      setShowMentionDropdown(true);
      calculateDropdownPosition();
    } else if (atIndex !== -1 && atIndex < cursorPos && showMentionDropdown) {
      // User is typing after @
      const searchText = textBeforeCursor.substring(atIndex + 1);
      if (/^\w*$/.test(searchText)) {
        // Valid search (letters, numbers, underscore only)
        setMentionSearch(searchText);
        calculateDropdownPosition();
      } else {
        // Invalid character, close dropdown
        setShowMentionDropdown(false);
      }
    } else if (showMentionDropdown) {
      // User moved cursor away from @ mention
      setShowMentionDropdown(false);
    }
  };

  // Calculate dropdown position relative to cursor
  const calculateDropdownPosition = () => {
    if (!textareaRef.current) return;

    const textBeforeCursor = comment.substring(0, cursorPosition);
    const lines = textBeforeCursor.split('\n');
    const currentLine = lines.length;
    const lineHeight = 24; // Approximate line height

    setMentionPosition({
      top: currentLine * lineHeight + 30,
      left: 10,
    });
  };

  // Handle employee selection from dropdown
  const handleEmployeeSelect = (employee) => {
    const beforeMention = comment.substring(0, mentionStartIndex);
    const afterCursor = comment.substring(cursorPosition);

    // Replace @search with @Name (display format)
    const newComment = `${beforeMention}@${employee.name}${afterCursor}`;
    setComment(newComment);
    setShowMentionDropdown(false);

    // Focus back on textarea
    setTimeout(() => {
      if (textareaRef.current) {
        const newCursorPos = mentionStartIndex + employee.name.length + 1;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  // Handle keyboard navigation in dropdown
  const handleKeyDown = (e) => {
    if (!showMentionDropdown) return;

    if (e.key === 'Escape') {
      setShowMentionDropdown(false);
      e.preventDefault();
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      // Could add keyboard navigation through dropdown here
      e.preventDefault();
    } else if (e.key === 'Enter' && filteredEmployees.length > 0) {
      // Select first employee on Enter
      handleEmployeeSelect(filteredEmployees[0]);
      e.preventDefault();
    }
  };

  // Handle submit
  const handleSubmit = () => {
    // Keep @Name mentions as plain text (AB Connect uses plain names)
    onSubmit(comment);
    resetModal();
  };

  // Handle skip
  const handleSkip = () => {
    onSkip();
    resetModal();
  };

  // Reset modal state
  const resetModal = () => {
    setComment('');
    setShowMentionDropdown(false);
    setMentionSearch('');
    setCursorPosition(0);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        textareaRef.current &&
        !textareaRef.current.contains(e.target)
      ) {
        setShowMentionDropdown(false);
      }
    };

    if (showMentionDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMentionDropdown]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      resetModal();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <Modal trackAs="ModelingStatusExitCommentModal" isOpen={isOpen} onClose={onClose} size="md" zIndex={10000}>
      {/* Header */}
      <div
        style={{
          padding: 'var(--space-6)',
          borderBottom: '1px solid var(--gray-700)',
          background: 'linear-gradient(to bottom, var(--dark-bg), var(--dark-surface))',
          borderTopLeftRadius: 'var(--radius-xl)',
          borderTopRightRadius: 'var(--radius-xl)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
          {/* Icon */}
          <div
            style={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              background: 'var(--cta-blue)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <MessageSquare size={28} color="white" />
          </div>

          {/* Title */}
          <div>
            <h1
              style={{
                fontSize: 'var(--text-2xl)',
                fontWeight: 600,
                color: 'var(--gray-50)',
                margin: 0,
              }}
            >
              Status Exit Comment
            </h1>
            <p
              style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--gray-400)',
                margin: 'var(--space-2) 0 0 0',
              }}
            >
              Leaving {fromStatus} → {toStatus}
            </p>
          </div>
        </div>

        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--gray-400)',
            cursor: 'pointer',
            padding: 'var(--space-2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <X size={20} />
        </button>
      </div>

      <ModalBody>
        <div style={{ padding: 'var(--space-6)' }}>
          <p
            style={{
              fontSize: 'var(--text-base)',
              color: 'var(--gray-300)',
              marginBottom: 'var(--space-4)',
            }}
          >
            Leave a comment for the next team (optional). Type <strong>@</strong> to mention team members.
          </p>

          {/* Textarea with mention support */}
          <div style={{ position: 'relative' }}>
            <textarea
              ref={textareaRef}
              value={comment}
              onChange={handleCommentChange}
              onKeyDown={handleKeyDown}
              placeholder="e.g., @Adam Boyte found some issues with the boundary lines..."
              className="modeling-status-exit-textarea"
              rows={6}
              style={{
                width: '100%',
                padding: 'var(--space-3)',
                fontSize: 'var(--text-base)',
                color: 'var(--gray-100)',
                background: 'var(--dark-surface)',
                border: '1px solid var(--gray-700)',
                borderRadius: 'var(--radius-md)',
                resize: 'vertical',
                fontFamily: 'inherit',
                lineHeight: '1.5',
              }}
            />

            {/* Mention Dropdown */}
            {showMentionDropdown && filteredEmployees.length > 0 && (
              <div
                ref={dropdownRef}
                className="mention-dropdown"
                style={{
                  position: 'absolute',
                  top: `${mentionPosition.top}px`,
                  left: `${mentionPosition.left}px`,
                  background: 'var(--dark-surface)',
                  border: '1px solid var(--gray-700)',
                  borderRadius: 'var(--radius-md)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  zIndex: 1000,
                  minWidth: '200px',
                }}
              >
                {filteredEmployees.map((emp) => (
                  <div
                    key={emp.id}
                    onClick={() => handleEmployeeSelect(emp)}
                    className="mention-dropdown-item"
                    style={{
                      padding: 'var(--space-3)',
                      cursor: 'pointer',
                      color: 'var(--gray-200)',
                      fontSize: 'var(--text-sm)',
                      borderBottom: '1px solid var(--gray-800)',
                    }}
                  >
                    {emp.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </ModalBody>

      <ModalFooter>
        <Button variant="ghost" onClick={handleSkip}>
          Skip
        </Button>
        <Button variant="primary" onClick={handleSubmit}>
          Submit Comment
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ModelingStatusExitCommentModal;
